import { ReactNode } from "react";
import { Accept, useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import { Stack, Typography } from "@mui/material";

import { useAlertContext } from "../alert/AlertContext";
import { FileListItem } from "../file-list-item/FileListItem";
import { formatFileSize } from "../format/formatFileSize";
import { DropZoneUploadButton, DropZoneUploadButtonSize } from "../upload/DropZoneUploadButton";
import { DROPZONE_ACCEPTED_FILES, DROPZONE_MAX_FILE_SIZE, notifyRejections } from "../upload/uploadNotifications";

interface Props {
  onDropFiles: (files: File[]) => void;
  pendingFiles: File[];
  onRemovePending: (index: number) => void;
  title?: string;
  hint?: string;
  uploadButtonPosition?: "top" | "bottom";
  uploadButtonSize?: DropZoneUploadButtonSize;
  uploadButtonLabel?: string;
  dense?: boolean;
  accept?: Accept;
  maxSize?: number;
  multiple?: boolean;
  gap?: number;
  children?: ReactNode;
}

export function FileUploadArea({
  onDropFiles,
  pendingFiles,
  onRemovePending,
  title,
  hint,
  uploadButtonPosition = "bottom",
  uploadButtonSize,
  uploadButtonLabel,
  dense = false,
  accept = DROPZONE_ACCEPTED_FILES,
  maxSize = DROPZONE_MAX_FILE_SIZE,
  multiple = true,
  gap = 2,
  children,
}: Props) {
  const { t } = useTranslation();
  const { showError } = useAlertContext();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      notifyRejections(rejectedFiles, showError, t);
      onDropFiles(acceptedFiles);
    },
    multiple,
    accept,
    maxSize,
  });

  const uploadButton = (
    <DropZoneUploadButton
      isDragActive={isDragActive}
      getRootProps={getRootProps}
      getInputProps={getInputProps}
      size={uploadButtonSize}
      label={uploadButtonLabel}
    />
  );

  return (
    <Stack gap={gap}>
      {title !== undefined && <Typography variant="h6">{title}</Typography>}
      {hint !== undefined && (
        <Typography variant="body2" color="text.secondary">
          {hint}
        </Typography>
      )}
      {uploadButtonPosition === "top" && uploadButton}
      {children}
      {pendingFiles.length > 0 && (
        <Stack gap={1}>
          {pendingFiles.map((file, index) => (
            <FileListItem
              key={index}
              fileName={file.name}
              contentType={dense ? undefined : file.type || undefined}
              sizeBytes={dense ? undefined : file.size}
              secondaryText={dense ? formatFileSize(file.size) : undefined}
              dense={dense}
              onDelete={() => onRemovePending(index)}
            />
          ))}
        </Stack>
      )}
      {uploadButtonPosition === "bottom" && uploadButton}
    </Stack>
  );
}
